/**
 * ConsentGuard Component
 * Redirects authenticated users to consent page if they haven't provided email marketing consent
 * or if their consent version is outdated
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserProfileContext } from '../../contexts/UserProfileContext';
import { needsConsent } from '../../utils/consentUtils';

interface ConsentGuardProps {
  children: React.ReactNode;
}

/**
 * ConsentGuard component
 * 
 * Checks if authenticated user needs to provide consent and redirects to /consent/email if needed.
 * Prevents redirect loop by not redirecting if already on the consent page.
 * 
 * @param props - Component props
 * @returns Children if consent is satisfied, null during loading or redirect
 */
export function ConsentGuard({ children }: ConsentGuardProps) {
  const { profile, isLoading } = useUserProfileContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't redirect if still loading or already on consent page
    if (isLoading || location.pathname === '/consent/email') {
      return;
    }

    // Redirect to consent page if needed
    if (needsConsent(profile)) {
      navigate('/consent/email', { replace: true });
    }
  }, [profile, isLoading, navigate, location]);

  // Only show loading state on initial profile fetch (no profile yet).
  // When profile already exists and is just being refreshed (e.g. after
  // credit consumption), keep rendering children to avoid unmounting them
  // and losing in-memory state like AI-generated compositions.
  if (isLoading && !profile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-accent text-sm">Loading...</div>
      </div>
    );
  }

  // Show nothing if consent is needed (will redirect via useEffect)
  if (needsConsent(profile)) {
    return null;
  }

  // Render children if consent is satisfied
  return <>{children}</>;
}
